import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Loader2, MessageCircle, MessageSquare, Forward, Send, Sheet, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPS_SCRIPT_CODE = `// Five Peaks Construction — Lead sync (paste into Google Apps Script)
function doPost(e) {
  const sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
  const data = JSON.parse(e.postData.contents);
  const lead = data.lead;
  const headers = ['ID','Created','Name','Email','Phone','Service','Project Type','Message','Status','Notes','Quote $','Next Action','Source','Email Sent','Last Synced'];
  if (sheet.getLastRow() === 0) {
    sheet.appendRow(headers);
    sheet.getRange(1, 1, 1, headers.length).setFontWeight('bold').setBackground('#1B2A4E').setFontColor('#ffffff');
    sheet.setFrozenRows(1);
  }
  const now = new Date();
  const row = [
    lead.id, lead.created_at, lead.name, lead.email, lead.phone,
    lead.service_type, lead.project_type, lead.message, lead.status,
    lead.notes || '', lead.quote_amount || '', lead.next_action_date || '',
    lead.source || 'website-form', lead.email_sent ? 'Yes' : 'No', now
  ];
  const rows = sheet.getDataRange().getValues();
  let target = -1;
  for (let i = 1; i < rows.length; i++) {
    if (String(rows[i][0]) === String(lead.id)) { target = i + 1; break; }
  }
  if (target > 0) {
    sheet.getRange(target, 1, 1, row.length).setValues([row]);
  } else {
    sheet.appendRow(row);
  }
  return ContentService.createTextOutput(JSON.stringify({ok:true})).setMimeType(ContentService.MimeType.JSON);
}
`;

export default function AdminSettings({ token }) {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    bot_enabled: true,
    bot_greeting: "",
    whatsapp_enabled: false,
    whatsapp_number: "",
    forward_chats_enabled: false,
    forward_email: "",
    sheets_sync_enabled: false,
    sheets_webhook_url: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingSheet, setTestingSheet] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [scriptCopied, setScriptCopied] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/settings`);
      setSettings(data);
      setForm({
        bot_enabled: !!data.bot_enabled,
        bot_greeting: data.bot_greeting || "",
        whatsapp_enabled: !!data.whatsapp_enabled,
        whatsapp_number: data.whatsapp_number || "",
        forward_chats_enabled: !!data.forward_chats_enabled,
        forward_email: data.forward_email || "",
        sheets_sync_enabled: !!data.sheets_sync_enabled,
        sheets_webhook_url: data.sheets_webhook_url || "",
      });
    } catch {
      toast.error("Failed to load settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/settings`, form, {
        headers: { "X-Admin-Token": token },
      });
      setSettings(data);
      setForm({
        bot_enabled: !!data.bot_enabled,
        bot_greeting: data.bot_greeting || "",
        whatsapp_enabled: !!data.whatsapp_enabled,
        whatsapp_number: data.whatsapp_number || "",
        forward_chats_enabled: !!data.forward_chats_enabled,
        forward_email: data.forward_email || "",
        sheets_sync_enabled: !!data.sheets_sync_enabled,
        sheets_webhook_url: data.sheets_webhook_url || "",
      });
      toast.success("Settings saved.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const sendTestForward = async () => {
    if (!form.forward_chats_enabled) {
      toast.error("Turn on forwarding first, then Save.");
      return;
    }
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/admin/chats/forward-test`, {}, {
        headers: { "X-Admin-Token": token },
      });
      toast.success(`Test transcript sent to ${data.to}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Test send failed.");
    } finally {
      setTesting(false);
    }
  };

  const sendTestSheet = async () => {
    if (!form.sheets_sync_enabled || !form.sheets_webhook_url) {
      toast.error("Enable sync, paste your webhook URL, then Save.");
      return;
    }
    setTestingSheet(true);
    try {
      await axios.post(`${API}/admin/sheets/test`, {}, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Test row sent — check your Google Sheet.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Test failed.");
    } finally {
      setTestingSheet(false);
    }
  };

  const syncAllLeads = async () => {
    if (!form.sheets_sync_enabled || !form.sheets_webhook_url) {
      toast.error("Enable sync, paste your webhook URL, then Save.");
      return;
    }
    if (!window.confirm("This will push every existing lead to your Google Sheet. Continue?")) return;
    setSyncingAll(true);
    try {
      const { data } = await axios.post(`${API}/admin/sheets/sync-all`, {}, {
        headers: { "X-Admin-Token": token },
      });
      toast.success(`Pushed ${data.pushed} lead${data.pushed === 1 ? "" : "s"} to your sheet.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sync failed.");
    } finally {
      setSyncingAll(false);
    }
  };

  const copyScript = async () => {
    try {
      await navigator.clipboard.writeText(APPS_SCRIPT_CODE);
      setScriptCopied(true);
      toast.success("Apps Script copied. Paste it into your sheet's Script editor.");
      setTimeout(() => setScriptCopied(false), 2500);
    } catch {
      toast.error("Could not copy — copy it manually from the box.");
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-border rounded-md p-12 text-center text-muted-foreground">
        <Loader2 className="w-5 h-5 mx-auto animate-spin" />
      </div>
    );
  }

  const digits = form.whatsapp_number.replace(/\D/g, "");
  const previewNumber = digits.length >= 10
    ? `+${digits.slice(0, digits.length - 10)} (${digits.slice(-10, -7)}) ${digits.slice(-7, -4)}-${digits.slice(-4)}`
    : form.whatsapp_number;

  return (
    <form onSubmit={save} data-testid="admin-settings" className="space-y-5">
      {/* Chat bot */}
      <div className="bg-white border border-border rounded-md p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 grid place-items-center bg-navy text-white rounded-md">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-display font-bold text-lg text-navy">AI Chat Assistant</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                Floating chat bubble on every page that answers visitor questions about your services.
                Turn off to hide it from the site entirely.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-bold uppercase tracking-wider ${form.bot_enabled ? "text-emerald-600" : "text-muted-foreground"}`}>
              {form.bot_enabled ? "On" : "Off"}
            </span>
            <Switch
              checked={form.bot_enabled}
              onCheckedChange={(v) => set("bot_enabled", v)}
              data-testid="toggle-bot-enabled"
            />
          </div>
        </div>

        <div className={`mt-6 ${form.bot_enabled ? "" : "opacity-60 pointer-events-none"}`}>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-navy">Greeting message</span>
            <Textarea
              data-testid="bot-greeting-input"
              value={form.bot_greeting}
              onChange={(e) => set("bot_greeting", e.target.value)}
              rows={3}
              className="mt-2 rounded-md"
              placeholder="Hi! I'm the Five Peaks assistant. How can I help?"
            />
          </label>
        </div>
      </div>

      {/* WhatsApp */}
      <div className="bg-white border border-border rounded-md p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 grid place-items-center bg-[#25D366] text-white rounded-md">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-display font-bold text-lg text-navy">WhatsApp Quick-Message Button</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                Floating WhatsApp button on the site. Visitors tap to open WhatsApp pre-filled with a
                message to the number below. Swap the number any time without touching code.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-bold uppercase tracking-wider ${form.whatsapp_enabled ? "text-emerald-600" : "text-muted-foreground"}`}>
              {form.whatsapp_enabled ? "On" : "Off"}
            </span>
            <Switch
              checked={form.whatsapp_enabled}
              onCheckedChange={(v) => set("whatsapp_enabled", v)}
              data-testid="toggle-whatsapp-enabled"
            />
          </div>
        </div>

        <div className={`mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 ${form.whatsapp_enabled ? "" : "opacity-60 pointer-events-none"}`}>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-navy">WhatsApp Number</span>
            <Input
              data-testid="whatsapp-number-input"
              value={form.whatsapp_number}
              onChange={(e) => set("whatsapp_number", e.target.value)}
              placeholder="+1 801-555-1234"
              className="mt-2 rounded-md"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Include the country code (e.g. 1 for USA). Letters and symbols are stripped automatically.
            </p>
          </label>
          <div className="bg-secondary rounded-md p-4">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Preview</div>
            <div className="mt-2 font-display text-navy">{previewNumber || "—"}</div>
            <div className="text-xs text-muted-foreground mt-1 break-all">
              {digits ? `https://wa.me/${digits}` : "(enable and enter a number)"}
            </div>
          </div>
        </div>
      </div>

      {/* Chat forwarding */}
      <div className="bg-white border border-border rounded-md p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 grid place-items-center bg-sunrise text-white rounded-md">
              <Forward className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-display font-bold text-lg text-navy">Forward Chats to Email</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                Email the full transcript when a visitor has chatted at least twice — so you can follow up
                on warm leads without checking the admin every day. Sent once per conversation.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-bold uppercase tracking-wider ${form.forward_chats_enabled ? "text-emerald-600" : "text-muted-foreground"}`}>
              {form.forward_chats_enabled ? "On" : "Off"}
            </span>
            <Switch
              checked={form.forward_chats_enabled}
              onCheckedChange={(v) => set("forward_chats_enabled", v)}
              data-testid="toggle-forward-enabled"
            />
          </div>
        </div>

        <div className={`mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 ${form.forward_chats_enabled ? "" : "opacity-60 pointer-events-none"}`}>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-navy">Forward to email</span>
            <Input
              data-testid="forward-email-input"
              type="email"
              value={form.forward_email}
              onChange={(e) => set("forward_email", e.target.value)}
              placeholder="leave blank to use FivepeakconstructionUT@gmail.com"
              className="mt-2 rounded-md"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Leave blank to send to your default business email.
            </p>
          </label>
          <div className="bg-secondary rounded-md p-4 flex flex-col justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Test it</div>
              <p className="text-xs text-muted-foreground mt-1">
                Send a sample transcript to verify the email arrives in your inbox.
              </p>
            </div>
            <Button
              type="button"
              onClick={sendTestForward}
              disabled={testing}
              data-testid="forward-test-btn"
              variant="outline"
              className="mt-3 self-start rounded-md"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Test Email
            </Button>
          </div>
        </div>
      </div>

      {/* Google Sheets CRM sync */}
      <div className="bg-white border border-border rounded-md p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 grid place-items-center bg-emerald-600 text-white rounded-md">
              <Sheet className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-display font-bold text-lg text-navy">Google Sheets CRM Sync</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                Auto-push every new lead into a Google Sheet so you have a full CRM with follow-up notes,
                quoted amounts and next-action dates in your Drive. Updates rows in place when you change
                a lead&apos;s status or notes — no duplicates.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-bold uppercase tracking-wider ${form.sheets_sync_enabled ? "text-emerald-600" : "text-muted-foreground"}`}>
              {form.sheets_sync_enabled ? "On" : "Off"}
            </span>
            <Switch
              checked={form.sheets_sync_enabled}
              onCheckedChange={(v) => set("sheets_sync_enabled", v)}
              data-testid="toggle-sheets-enabled"
            />
          </div>
        </div>

        <details className="mt-5 group">
          <summary className="cursor-pointer text-sm font-semibold text-navy hover:text-sunrise list-none flex items-center gap-2">
            <span className="text-sunrise group-open:rotate-90 transition-transform inline-block">▸</span>
            Setup instructions (one-time, ~3 minutes)
          </summary>
          <ol className="mt-4 space-y-3 text-sm text-muted-foreground list-decimal pl-5">
            <li>
              In Google Drive, click <strong>+ New → Google Sheets</strong>. Name it
              <em> &quot;Five Peaks — Leads CRM&quot;</em>.
            </li>
            <li>
              Inside the sheet, open <strong>Extensions → Apps Script</strong>.
            </li>
            <li>
              Delete any boilerplate code. Click the button below to copy our script, then paste it into
              the Apps Script editor. Save (the disk icon).
              <div className="mt-2 flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={copyScript}
                  className="rounded-md"
                  data-testid="copy-script-btn"
                >
                  {scriptCopied ? <Check className="w-4 h-4 mr-2 text-emerald-600" /> : <Copy className="w-4 h-4 mr-2" />}
                  {scriptCopied ? "Copied!" : "Copy Apps Script"}
                </Button>
              </div>
              <pre className="mt-2 bg-secondary border border-border rounded-md p-3 text-[11px] overflow-x-auto max-h-44 whitespace-pre">{APPS_SCRIPT_CODE}</pre>
            </li>
            <li>
              In the Apps Script editor click <strong>Deploy → New deployment</strong>. Type:
              <strong> Web app</strong>. &quot;Who has access&quot;: <strong>Anyone</strong>. Click Deploy.
            </li>
            <li>
              Authorize access (Google may show a warning — click &quot;Advanced → Go to project (unsafe)&quot;,
              then &quot;Allow&quot;).
            </li>
            <li>
              Copy the <strong>Web app URL</strong> Google gives you (ends in <code className="bg-secondary px-1 rounded">/exec</code>) and paste it in the box below.
            </li>
            <li>Enable the switch above, click <strong>Save Settings</strong>, then hit <strong>Send Test Row</strong>.</li>
          </ol>
        </details>

        <div className={`mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 ${form.sheets_sync_enabled ? "" : "opacity-60 pointer-events-none"}`}>
          <label className="block md:col-span-2">
            <span className="text-xs font-bold uppercase tracking-wider text-navy">Web app URL</span>
            <Input
              data-testid="sheets-url-input"
              value={form.sheets_webhook_url}
              onChange={(e) => set("sheets_webhook_url", e.target.value)}
              placeholder="https://script.google.com/macros/s/.../exec"
              className="mt-2 rounded-md text-xs"
            />
          </label>
          <div className="bg-secondary rounded-md p-4 flex flex-col gap-2">
            <Button
              type="button"
              onClick={sendTestSheet}
              disabled={testingSheet}
              data-testid="sheets-test-btn"
              variant="outline"
              className="rounded-md w-full justify-center"
            >
              {testingSheet ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Test Row
            </Button>
            <Button
              type="button"
              onClick={syncAllLeads}
              disabled={syncingAll}
              data-testid="sheets-sync-all-btn"
              variant="outline"
              className="rounded-md w-full justify-center"
            >
              {syncingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sheet className="w-4 h-4 mr-2" />}
              Sync All Existing Leads
            </Button>
          </div>
        </div>
      </div>

      {/* Save bar */}
      <div className="bg-white border border-border rounded-md p-4 flex items-center justify-between flex-wrap gap-3 sticky bottom-4 elev-2">
        <div className="text-xs text-muted-foreground">
          {settings?.updated_at ? `Last saved ${new Date(settings.updated_at).toLocaleString()}` : "Unsaved changes"}
        </div>
        <Button
          type="submit"
          disabled={saving}
          data-testid="settings-save"
          className="bg-sunrise hover:bg-sunrise-hover rounded-md"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>
    </form>
  );
}
